/**
 * useSensorLayer — renders the tenant's deployed sensors on a Cesium
 * viewer as color-coded ground pins. Uses a single CustomDataSource so
 * toggling the layer off cleanly hides every marker.
 *
 * Data: `sensorsApi.list()` polled every 30 s. Matches the cadence of
 * `SystemAlertBanner` so offline sensors surface both on the banner and
 * in the operator's visual scan of the map with the same timing.
 *
 * Sensor text is intentionally not rendered as Cesium labels. The label
 * atlas can produce colored speckle at some scales, so clicks on a sensor
 * billboard open a DOM tooltip instead.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Viewer as CesiumViewer,
  CustomDataSource,
  Cartesian3,
  NearFarScalar,
  VerticalOrigin,
} from 'cesium';
import { sensorsApi } from '../services/api';
import { SENSOR_ICON_DATA_URI } from '../components/map/icons';
import { useMapStore } from '../store/mapStore';

const DATASOURCE_NAME = 'sensor-coverage';

export interface SensorMapSnapshot {
  id: string;
  name: string;
  status: string;
  latitude: number;
  longitude: number;
  altitudeMsl: number | null;
}

interface SensorRow extends Omit<SensorMapSnapshot, 'latitude' | 'longitude'> {
  latitude: number | null;
  longitude: number | null;
}

const SENSOR_BY_ENTITY_ID = new Map<string, SensorMapSnapshot>();

export function getSensorByEntityId(entityId: string): SensorMapSnapshot | undefined {
  return SENSOR_BY_ENTITY_ID.get(entityId);
}

export function useSensorLayer(viewer: CesiumViewer | null, visible: boolean) {
  const dataSourceRef = useRef<CustomDataSource | null>(null);
  const setSensorMarkers = useMapStore((s) => s.setSensorMarkers);

  // 30 s poll matches SystemAlertBanner so a sensor going offline shows up
  // on the map and in the banner on the same refresh cycle.
  const { data } = useQuery({
    queryKey: ['sensors', 'map'],
    queryFn: () => sensorsApi.list({ pageSize: 100 }),
    refetchInterval: 30_000,
    enabled: visible && !!viewer,
  });

  // Attach / detach the datasource when the viewer or visibility changes.
  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    if (!dataSourceRef.current) {
      const ds = new CustomDataSource(DATASOURCE_NAME);
      dataSourceRef.current = ds;
      viewer.dataSources.add(ds);
    }
    dataSourceRef.current.show = visible;
    if (!visible) {
      setSensorMarkers([]);
    }

    return () => {
      // Cleanup only when the viewer actually unmounts — leaving the
      // datasource attached across visibility flips keeps the entity
      // set warm (we just toggle `.show`).
      if (viewer.isDestroyed()) return;
    };
  }, [setSensorMarkers, viewer, visible]);

  // Upsert sensor entities whenever the query data changes.
  useEffect(() => {
    const ds = dataSourceRef.current;
    if (!ds || !viewer || viewer.isDestroyed()) return;

    const sensors: SensorRow[] = (data?.data as SensorRow[] | undefined) ?? [];
    // A sensor without coordinates can't be placed — drop silently.
    const placed = sensors.filter(
      (s) =>
        typeof s.latitude === 'number' &&
        typeof s.longitude === 'number' &&
        !(s.latitude === 0 && s.longitude === 0),
    );
    const snapshots: SensorMapSnapshot[] = placed.map((s) => ({
      id: s.id,
      name: s.name,
      status: s.status,
      latitude: s.latitude as number,
      longitude: s.longitude as number,
      altitudeMsl: s.altitudeMsl,
    }));
    setSensorMarkers(visible ? snapshots : []);

    // Each sensor renders as one billboard entity. Details are mirrored to
    // mapStore so a DOM click target can open the tooltip without relying
    // on Cesium's GPU picking.
    const currentIds = new Set<string>();
    for (const snapshot of snapshots) {
      currentIds.add(`sensor-${snapshot.id}`);
    }
    for (const entity of ds.entities.values.slice()) {
      if (!currentIds.has(entity.id)) {
        ds.entities.removeById(entity.id);
        SENSOR_BY_ENTITY_ID.delete(entity.id);
      }
    }

    for (const snapshot of snapshots) {
      const entityId = `sensor-${snapshot.id}`;
      const position = Cartesian3.fromDegrees(
        snapshot.longitude,
        snapshot.latitude,
        snapshot.altitudeMsl ?? 0,
      );
      SENSOR_BY_ENTITY_ID.set(entityId, snapshot);

      // Sensor marker. Text details render in a React tooltip after click.
      const main = ds.entities.getById(entityId);
      if (main) {
        main.position = position as any;
        main.properties = undefined as any;
        main.label = undefined as any;
      } else {
        ds.entities.add({
          id: entityId,
          position,
          billboard: {
            image: SENSOR_ICON_DATA_URI,
            width: 48,
            height: 48,
            // No heightReference: render at the sensor's true Cartesian
            // position (matches the label). CLAMP_TO_GROUND would snap to
            // terrain or — worse — to the top of any 3D tileset rendered at
            // the same lat/lng (e.g. FAA facility extrusions), moving the
            // icon away from where the sensor actually is.
            verticalOrigin: VerticalOrigin.BOTTOM,
            scaleByDistance: new NearFarScalar(500, 1.0, 120000, 0.45),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
      }
    }
  }, [data, setSensorMarkers, viewer, visible]);

  // Fully unmount the datasource when the viewer is torn down.
  useEffect(() => {
    return () => {
      const v = viewer;
      const ds = dataSourceRef.current;
      if (v && !v.isDestroyed() && ds) {
        for (const entity of ds.entities.values) {
          SENSOR_BY_ENTITY_ID.delete(entity.id);
        }
        v.dataSources.remove(ds, true);
      }
      setSensorMarkers([]);
      dataSourceRef.current = null;
    };
  }, [setSensorMarkers, viewer]);
}
